import { ContentWarning, MediaWarningOverlay } from '@/components/ContentWarning'
import { Avatar } from '@/components/Avatar'
import { PostAuthorRow } from '@/components/PostAuthorRow'
import { RichText } from '@/components/RichText'
import { VideoPlayer } from '@/components/VideoPlayer'
import { isGifUrl, extractYouTubeVideoId, extractSpotifyInfo, extractTweetId } from '@/lib/embed'
import { YouTubeEmbed } from '@/components/YouTubeEmbed'
import { SpotifyEmbed } from '@/components/SpotifyEmbed'
import { TwitterEmbed } from '@/components/TwitterEmbed'
import { StandardSiteReadInAppCTA } from '@/components/StandardSiteReadInAppCTA'
import { StandardSiteArticleCard } from '@/components/StandardSiteArticleCard'
import { parseStandardDocumentUri } from '@/lib/standardSite'
import { getPostBridgeInfo } from '@/lib/bridge'
import { isTruncatedBridgedPost, stripQuoteReplyMarker } from '@/lib/bridgedContent'
import { PostCountsRow } from '@/components/PostCountsRow'
import { QUOTED_POST_CARD_CLASS } from './quotedPostCardStyles'
import type { QuotedPostEmbedProps } from './QuotedPostEmbed.types'
import type { PostEmbed } from '@/types/posts'

export function QuotedPostEmbed({
    post,
    onAuthorClick,
    onPostClick,
    compact,
    showCounts,
}: QuotedPostEmbedProps) {
    const bridgeInfo = getPostBridgeInfo(post.author, post.bridgyOriginalUrl)
    const isTruncated = isTruncatedBridgedPost(post.text)
    const displayText = stripQuoteReplyMarker(post.text)

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onPostClick && (post.isActivityPub ? post.uri : post.rkey)) {
            onPostClick(post)
        }
    }

    const handleAuthorClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onAuthorClick && post.author) {
            onAuthorClick(post.author.handle)
        }
    }

    return (
        <div className={QUOTED_POST_CARD_CLASS} onClick={handleClick}>
            <div className="p-3">
                {/* Author row */}
                <div className="flex items-center gap-1.5 min-w-0">
                    <button
                        onClick={handleAuthorClick}
                        className="flex-shrink-0 focus:outline-none"
                        disabled={!onAuthorClick}
                    >
                        <Avatar
                            src={post.author?.avatarUrl}
                            alt={post.author?.handle ?? 'user'}
                            size="xs"
                        />
                    </button>
                    <PostAuthorRow
                        author={post.author}
                        createdAt={post.createdAt}
                        protocol={post.protocol}
                        isActivityPub={post.isActivityPub}
                        bridgeInfo={bridgeInfo}
                        onAuthorClick={() => post.author && onAuthorClick?.(post.author.handle)}
                        onBridgedHandleClick={() =>
                            bridgeInfo && onAuthorClick?.(bridgeInfo.bridgedHandle)
                        }
                        authorClickDisabled={!onAuthorClick}
                        size="xs"
                        showTimestamp={false}
                    />
                </div>

                {/* Post content — wrapped by content warning when present */}
                {post.contentWarning?.scope === 'content' ? (
                    <ContentWarning text={post.contentWarning.text}>
                        {displayText && (
                            <div className="mt-1 line-clamp-4">
                                <RichText
                                    text={displayText}
                                    facets={post.facets}
                                    className="text-sm text-text-primary"
                                />
                            </div>
                        )}
                    </ContentWarning>
                ) : (
                    <>
                        {displayText && (
                            <div className="mt-1 line-clamp-4">
                                <RichText
                                    text={displayText}
                                    facets={post.facets}
                                    className="text-sm text-text-primary"
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Nested embed — outside the p-3 div for full-bleed display in all cases */}
            {post.embed &&
                (post.contentWarning?.scope === 'content' ? (
                    <QuotedEmbed embed={post.embed} compact={compact} isTruncated={isTruncated} />
                ) : post.contentWarning?.scope === 'media' ? (
                    <MediaWarningOverlay text={post.contentWarning.text}>
                        <QuotedEmbed
                            embed={post.embed}
                            compact={compact}
                            isTruncated={isTruncated}
                        />
                    </MediaWarningOverlay>
                ) : (
                    <QuotedEmbed embed={post.embed} compact={compact} isTruncated={isTruncated} />
                ))}

            {/* Read-only counts, after any full-bleed embed. Renders nothing when absent. */}
            {showCounts && (
                <PostCountsRow
                    replyCount={post.metrics?.replyCount}
                    repostCount={post.metrics?.repostCount}
                    likeCount={post.metrics?.likeCount}
                    isActivityPub={post.isActivityPub}
                    className={`px-3 pb-3${post.embed ? ' pt-3' : ''}`}
                />
            )}
        </div>
    )
}

function QuotedEmbed({
    embed,
    compact,
    isTruncated,
}: {
    embed: PostEmbed
    compact?: boolean
    isTruncated?: boolean
}) {
    if (isTruncated && embed.type === 'external') return null

    if (embed.type === 'images' && embed.images?.length) {
        const img = embed.images[0]
        return (
            <div className="border-t border-border-light dark:border-border-dark">
                <img
                    src={img.url}
                    alt={img.alt ?? ''}
                    className="w-full object-cover"
                    onError={e => {
                        const parent = e.currentTarget.parentElement
                        if (parent) parent.style.display = 'none'
                    }}
                />
            </div>
        )
    }

    if (embed.type === 'video' && embed.video) {
        return (
            <div
                className={`border-t border-border-light dark:border-border-dark${compact ? ' max-h-[200px] overflow-hidden' : ''}`}
            >
                <VideoPlayer
                    playlist={embed.video.playlist}
                    thumbnail={embed.video.thumbnail}
                    aspectRatio={embed.video.aspectRatio}
                    alt={embed.video.alt}
                />
            </div>
        )
    }

    if (embed.type === 'external' && embed.external) {
        if (isGifUrl(embed.external.uri)) {
            return (
                <div className="border-t border-border-light dark:border-border-dark">
                    <img
                        src={embed.external.uri}
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty description/title should fall through to the next candidate, not render blank
                        alt={embed.external.description || embed.external.title || 'GIF'}
                        className="w-full min-h-[100px] object-contain bg-black/5 dark:bg-white/5"
                        onError={e => {
                            const parent = e.currentTarget.parentElement
                            if (parent) parent.style.display = 'none'
                        }}
                    />
                </div>
            )
        }

        // Render YouTube links as embedded players
        const youtubeId = extractYouTubeVideoId(embed.external.uri)
        if (youtubeId) {
            return (
                <div
                    className={`border-t border-border-light dark:border-border-dark${compact ? ' max-h-[200px] overflow-hidden' : ''}`}
                    onClick={e => e.stopPropagation()}
                >
                    <YouTubeEmbed videoId={youtubeId} title={embed.external.title} />
                </div>
            )
        }

        // Render Spotify links as embedded players
        const spotifyInfo = extractSpotifyInfo(embed.external.uri)
        if (spotifyInfo) {
            return (
                <div
                    className={`border-t border-border-light dark:border-border-dark${compact ? ' max-h-[200px] overflow-hidden' : ''}`}
                    onClick={e => e.stopPropagation()}
                >
                    <SpotifyEmbed
                        type={spotifyInfo.type}
                        id={spotifyInfo.id}
                        title={embed.external.title}
                    />
                </div>
            )
        }

        // Render Twitter/X links as embedded posts
        const tweetId = extractTweetId(embed.external.uri)
        if (tweetId) {
            return (
                <div
                    className={`border-t border-border-light dark:border-border-dark${compact ? ' max-h-[200px] overflow-hidden' : ''}`}
                    onClick={e => e.stopPropagation()}
                >
                    <TwitterEmbed tweetId={tweetId} title={embed.external.title} />
                </div>
            )
        }

        if (embed.external.source && parseStandardDocumentUri(embed.associatedRefs)) {
            if (compact) {
                const sourceLabel =
                    embed.external.source?.title ??
                    (() => {
                        try {
                            return new URL(embed.external.uri).hostname
                        } catch {
                            return embed.external.uri
                        }
                    })()
                return (
                    <div className="border-t border-border-light dark:border-border-dark px-3 py-2 flex items-start gap-2">
                        {embed.external.thumb && (
                            <img
                                src={embed.external.thumb}
                                alt={embed.external.title ?? ''}
                                className="h-12 w-16 object-cover rounded flex-shrink-0"
                                loading="lazy"
                                onError={e => {
                                    e.currentTarget.style.display = 'none'
                                }}
                            />
                        )}
                        <div className="min-w-0 flex-1">
                            {embed.external.title && (
                                <p className="text-xs font-semibold text-text-primary line-clamp-2">
                                    {embed.external.title}
                                </p>
                            )}
                            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark truncate">
                                {sourceLabel}
                            </p>
                        </div>
                    </div>
                )
            }
            return <StandardSiteArticleCard embed={embed} />
        }

        if (compact) {
            return (
                <div className="border-t border-border-light dark:border-border-dark px-3 py-2 flex items-start gap-2">
                    {embed.external.thumb && (
                        <img
                            src={embed.external.thumb}
                            alt={embed.external.title ?? ''}
                            className="h-12 w-16 object-cover rounded flex-shrink-0"
                            onError={e => {
                                e.currentTarget.style.display = 'none'
                            }}
                        />
                    )}
                    <div className="min-w-0 flex-1">
                        {embed.external.title && (
                            <p className="text-xs font-semibold text-text-primary line-clamp-2">
                                {embed.external.title}
                            </p>
                        )}
                        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark truncate">
                            {(() => {
                                try {
                                    return new URL(embed.external.uri).hostname
                                } catch {
                                    return embed.external.uri
                                }
                            })()}
                        </p>
                    </div>
                </div>
            )
        }

        return (
            <>
                <div className="border-t border-border-light dark:border-border-dark px-3 py-2">
                    {embed.external.thumb && (
                        <img
                            src={embed.external.thumb}
                            alt={embed.external.title ?? ''}
                            className="w-full h-24 object-cover rounded mb-1.5"
                            onError={e => {
                                e.currentTarget.style.display = 'none'
                            }}
                        />
                    )}
                    {embed.external.title && (
                        <p className="text-xs font-semibold text-text-primary line-clamp-1">
                            {embed.external.title}
                        </p>
                    )}
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark truncate">
                        {(() => {
                            try {
                                return new URL(embed.external.uri).hostname
                            } catch {
                                return embed.external.uri
                            }
                        })()}
                    </p>
                </div>
                <div className="px-3 pb-2">
                    <StandardSiteReadInAppCTA embed={embed} />
                </div>
            </>
        )
    }

    return null
}
